/**
 * Return all user-readable messages contained in an API error value.
 *
 * The API currently returns validation errors in `data`, while other
 * endpoints may use the conventional `errors` property. Supporting both
 * shapes here keeps individual screens from having to know the response
 * envelope and makes malformed/partial responses safe to handle.
 */
const collectMessages = (value) => {
  if (typeof value === "string") {
    const message = value.trim();
    return message ? [message] : [];
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectMessages);
  }

  if (value && typeof value === "object") {
    return Object.values(value).flatMap(collectMessages);
  }

  return [];
};

const uniqueMessages = (messages) => [...new Set(messages)];

/**
 * Extract a useful message from the supported API error envelopes.
 *
 * Field-level messages are intentionally checked before the top-level
 * message. For example, `{ message: "Validation error", data: { ... } }`
 * should show the actionable field error rather than the generic label.
 * `payload` may be a parsed response body, an Axios-style error, or an Error.
 */
export const getApiErrorMessage = (
  payload,
  fallback = "Something went wrong. Please try again."
) => {
  const body = payload?.response?.data ?? payload;

  const detailedMessages = uniqueMessages([
    ...collectMessages(body?.data),
    ...collectMessages(body?.errors),
  ]);

  if (detailedMessages.length) {
    return detailedMessages.join(" ");
  }

  const summaryMessages = uniqueMessages([
    ...collectMessages(body?.message),
    ...collectMessages(body?.error),
  ]);

  if (summaryMessages.length) {
    return summaryMessages[0];
  }

  // Some gateways return a plain-text response instead of a JSON envelope.
  const plainMessage =
    typeof body === "string" || typeof body === "number" || typeof body === "boolean"
      ? collectMessages(body)[0]
      : null;
  return plainMessage || fallback;
};

